import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import LoginRequired from '../hoc/LoginRequired';
import { PermissionKey } from '../utils/permission';
import { AdmissionPage } from './admission/AdmissionPage';
import { SimulatedApplicationPage } from './admission/SimulatedApplicationPage';
import { CompetitiveUnitAdmissionPage } from './admission/competitiveUnit/CompetitiveUnitAdmissionPage';
import { CompetitiveUnitPage } from './admission/competitiveUnit/CompetitiveUnitPage';
import BadgeApplicationPage from './community/badge-application/BadgeApplicationPage';
import { AdmissionApplyCountPage } from './mock/AdmissionApplyCountPage';
import App from './root/App';
import { StandarizedSubjectPage } from './subject/StandarizedSubjectPage';

const CategoryPage = lazy(() => import('./community/CategoryPage'));
const TopicPage = lazy(() => import('./community/TopicPage'));
const TopicDetailPage = lazy(() => import('./community/topic/TopicDetailPage'));
const TopicWritePage = lazy(() => import('./community/topic/TopicWritePage'));
const BotPolicyPage = lazy(() => import('./community/BotPolicyPage'));
const BotPolicyDetailPage = lazy(() => import('./community/bot-policy/BotPolicyDetailPage'));
const BotPolicyWritePage = lazy(() => import('./community/bot-policy/BotPolicyWritePage'));
const PersonaPage = lazy(() => import('./community/PersonaPage'));
const PersonaDetailPage = lazy(() => import('./community/persona/PersonaDetailPage'));
const PersonaWritePage = lazy(() => import('./community/persona/PersonaWritePage'));
const PromptTemplatePage = lazy(() => import('./community/PromptTemplatePage'));
const PromptTemplateDetailPage = lazy(
  () => import('./community/prompt-template/PromptTemplateDetailPage'),
);
const PromptTemplateWritePage = lazy(
  () => import('./community/prompt-template/PromptTemplateWritePage'),
);
const BotGuardrailRulePage = lazy(() => import('./community/BotGuardrailRulePage'));
const BotGuardrailRuleDetailPage = lazy(
  () => import('./community/guardrail-rule/BotGuardrailRuleDetailPage'),
);
const BotGuardrailRuleWritePage = lazy(
  () => import('./community/guardrail-rule/BotGuardrailRuleWritePage'),
);
const BotGuardrailLogPage = lazy(() => import('./community/BotGuardrailLogPage'));
const BotLogPage = lazy(() => import('./community/BotLogPage'));
const FaqPage = lazy(() => import('./community/FaqPage'));
const FaqDetailPage = lazy(() => import('./community/faq/FaqDetailPage'));
const FaqWritePage = lazy(() => import('./community/faq/FaqWritePage'));
const PostPage = lazy(() => import('./community/PostPage'));
const InquiryPage = lazy(() => import('./community/inquiry/InquiryPage'));
const InquiryDetailPage = lazy(() => import('./community/inquiry/InquiryDetailPage'));
const InquiryAnswerEditPage = lazy(() => import('./community/inquiry/InquiryAnswerEditPage'));
const NoticePage = lazy(() => import('./community/notice/NoticePage'));
const NoticeDetailPage = lazy(() => import('./community/notice/NoticeDetailPage'));
const NoticeWritePage = lazy(() => import('./community/notice/NoticeWritePage'));
const AdmissionsPage = lazy(() => import('./community/notice/NoticePage'));
const BadgePage = lazy(() => import('./database/BadgePage'));
const CountryPage = lazy(() => import('./database/CountryPage'));
const HighSchoolPage = lazy(() => import('./database/HighSchoolPage'));
const ItemPage = lazy(() => import('./database/ItemPage'));
const StatisticsMajorCategoryPage = lazy(() => import('./database/StatisticsMajorCategoryPage'));
const UniversityPage = lazy(() => import('./database/UniversityPage'));
const FakeApplicantPage = lazy(() => import('./mock/FakeApplicantPage'));
const PointHistoryPage = lazy(() => import('./point/PointHistoryPage'));
const UserPaymentPage = lazy(() => import('./point/UserItemPage'));
const UserPointPaymentPage = lazy(() => import('./point/UserPointPaymentPage'));
const ReportPage = lazy(() => import('./saction/ReportPage'));
const RestrictPage = lazy(() => import('./saction/RestrictPage'));
const UserDetailPage = lazy(() => import('./user/UserDetailPage'));
const SubjectReviewPage = lazy(() => import('./subjectReview/SubjectReviewPage'));
const TutorApplicationPage = lazy(() => import('./tutor/TutorApplicationPage'));
const TutorPage = lazy(() => import('./tutor/TutorPage'));
const ApplicantPage = lazy(() => import('./mock/ApplicantPage'));
const LoginPage = lazy(() => import('./login/LoginPage'));
const Mainpage = lazy(() => import('./root/Mainpage'));
const SettingPage = lazy(() => import('./system/SettingPage'));
const UserPage = lazy(() => import('./user/UserPage'));
const AppPushAlarmPage = lazy(() => import('./system/AppPushPage'));
const PassEssayPage = lazy(() => import('./pass-essay/PassEssayPage'));
const PassEssayApplicationPage = lazy(() => import('./pass-essay/PassEssayApplicationPage'));
const SimpleAdmissionCompetitionPage = lazy(
  () => import('./simple-admission/SimpleAdmissionCompetitionPage'),
);
const SimpleAdmissionCompetitionDetailPage = lazy(
  () => import('./simple-admission/SimpleAdmissionCompetitionDetailPage'),
);
const SimpleAdmissionApplicationPage = lazy(
  () => import('./simple-admission/SimpleAdmissionApplicationPage'),
);
const SimpleAdmissionApplicationDetailPage = lazy(
  () => import('./simple-admission/SimpleAdmissionApplicationDetailPage'),
);
const SimpleAdmissionShareAttemptPage = lazy(
  () => import('./simple-admission/SimpleAdmissionShareAttemptPage'),
);
const SimpleAdmissionShareAttemptDetailPage = lazy(
  () => import('./simple-admission/SimpleAdmissionShareAttemptDetailPage'),
);
const StatisticsUserAdmissionPage = lazy(() => import('./statistics/StatisticsUserAdmissionPage'));
const StatisticsUserAdmissionReportPage = lazy(
  () => import('./statistics/StatisticsUserAdmissionReportPage'),
);
const StatisticsUserAdmissionDetailPage = lazy(
  () => import('./statistics/StatisticsUserAdmissionDetailPage'),
);
const StatisticsExcelUploadLogsPage = lazy(
  () => import('./statistics/StatisticsExcelUploadLogsPage'),
);
const StatisticsExcelUploadLogDetailPage = lazy(
  () => import('./statistics/StatisticsExcelUploadLogDetailPage'),
);
const StatisticsExcelDownloadLogsPage = lazy(
  () => import('./statistics/StatisticsExcelDownloadLogsPage'),
);
const ProblemSolvedManagePage = lazy(() => import('./problem-solved/ProblemSolvedManagePage'));
const ProblemErrorPage = lazy(() => import('./problem-error'));
const QuestionFormPage = lazy(() => import('./problem-solved/question/QuestionFormPage'));
const ExamRoundListPage = lazy(() => import('./exam-round/ExamRoundListPage'));
const ExamRoundFormPage = lazy(() => import('./exam-round/ExamRoundFormPage'));
const ProblemSolvingSessionListPage = lazy(
  () => import('./problem-solving-sessions/ProblemSolvingSessionListPage'),
);
const ProblemSolvingSessionDashboardPage = lazy(
  () => import('./problem-solving-sessions/ProblemSolvingSessionDashboardPage'),
);
const ProblemSolvingSessionDetailPage = lazy(
  () => import('./problem-solving-sessions/ProblemSolvingSessionDetailPage'),
);

export const router: RouteObject[] = [
  {
    path: '/statistics/user-admissions/report',
    element: (
      <LoginRequired requiredPermissions={[PermissionKey.STATISTICS]}>
        <Suspense fallback={<>...loading</>}>
          <StatisticsUserAdmissionReportPage />
        </Suspense>
      </LoginRequired>
    ),
  },
  {
    path: '/',
    element: <App />,
    errorElement: <App.Error />,
    children: [
      {
        path: '/',
        element: <Outlet />,
        children: [
          {
            path: '/',
            element: (
              <LoginRequired>
                <Suspense fallback={<>...loading</>}>
                  <Mainpage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/user',
        element: <Outlet />,
        children: [
          {
            path: '/user',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.USER_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <UserPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/user/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.USER_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <UserDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/point',
        element: <Outlet />,
        children: [
          {
            path: '/point/user-item',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.POINT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <UserPaymentPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/point/payment-history',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.POINT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <UserPointPaymentPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/point/point-history',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.POINT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PointHistoryPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/admission',
        element: <Outlet />,
        children: [
          {
            path: '/admission/simulatedApplication',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimulatedApplicationPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/admission/admission',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <AdmissionPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/admission/competitiveUnit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <CompetitiveUnitPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/admission/competitiveUnit/:competitiveUnitId',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <CompetitiveUnitAdmissionPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/simple-admission-admin',
        element: <Outlet />,
        children: [
          {
            path: '/simple-admission-admin/competitions',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionCompetitionPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/simple-admission-admin/competitions/:admissionId',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionCompetitionDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/simple-admission-admin/applications',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionApplicationPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/simple-admission-admin/applications/:applicationId',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionApplicationDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/simple-admission-admin/share-attempts',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionShareAttemptPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/simple-admission-admin/share-attempts/:shareAttemptId',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SIMULATED_APPLICATION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SimpleAdmissionShareAttemptDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/subject-review',
        element: (
          <LoginRequired requiredPermissions={[PermissionKey.ADMISSION_MANAGEMENT]}>
            <Suspense fallback={<>...loading</>}>
              <SubjectReviewPage />
            </Suspense>
          </LoginRequired>
        ),
      },
      {
        path: '/tutor-application',
        element: (
          <LoginRequired requiredPermissions={[PermissionKey.ADMISSION_MANAGEMENT]}>
            <Suspense fallback={<>...loading</>}>
              <TutorApplicationPage />
            </Suspense>
          </LoginRequired>
        ),
      },
      {
        path: '/tutor',
        element: (
          <LoginRequired requiredPermissions={[PermissionKey.ADMISSION_MANAGEMENT]}>
            <Suspense fallback={<>...loading</>}>
              <TutorPage />
            </Suspense>
          </LoginRequired>
        ),
      },
      {
        path: '/pass-essay',
        element: (
          <LoginRequired requiredPermissions={[PermissionKey.ADMISSION_MANAGEMENT]}>
            <Suspense>
              <PassEssayPage />
            </Suspense>
          </LoginRequired>
        ),
      },
      {
        path: '/pass-essay-application',
        element: (
          <LoginRequired requiredPermissions={[PermissionKey.ADMISSION_MANAGEMENT]}>
            <Suspense>
              <PassEssayApplicationPage />
            </Suspense>
          </LoginRequired>
        ),
      },
      {
        path: '/community',
        element: <Outlet />,
        children: [
          {
            path: '/community/notice',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <NoticePage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/notice/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <NoticeWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/notice/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <NoticeWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/notice/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <NoticeDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/faq',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <FaqPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/faq/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <FaqWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/faq/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <FaqWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/faq/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <FaqDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/category',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <CategoryPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/inquiry',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <InquiryPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/inquiry/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <InquiryAnswerEditPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/inquiry/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <InquiryDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },

          {
            path: '/community/post',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PostPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community/badge-application',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BadgeApplicationPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/community-bot',
        element: <Outlet />,
        children: [
          {
            path: '/community-bot/topic',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <TopicPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/topic/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <TopicWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/topic/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <TopicWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/topic/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <TopicDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-policy',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotPolicyPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-policy/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotPolicyWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-policy/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotPolicyWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-policy/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotPolicyDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/persona',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PersonaPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/persona/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PersonaWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/persona/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PersonaWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/persona/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PersonaDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/prompt-template',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PromptTemplatePage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/prompt-template/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PromptTemplateWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/prompt-template/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PromptTemplateWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/prompt-template/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <PromptTemplateDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-log',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotLogPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-guardrail-rule',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotGuardrailRulePage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-guardrail-rule/write',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotGuardrailRuleWritePage mode='add' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-guardrail-rule/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotGuardrailRuleWritePage mode='edit' />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-guardrail-rule/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotGuardrailRuleDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/community-bot/bot-guardrail-log',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.COMMUNITY_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BotGuardrailLogPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/mock',
        element: <Outlet />,
        children: [
          {
            path: '/mock/fakeApplicant',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.MOCK_APPLICANT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <FakeApplicantPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/mock/applicant',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.MOCK_APPLICANT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ApplicantPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/mock/apply-count',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.MOCK_APPLICANT_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <AdmissionApplyCountPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/sanction',
        element: <Outlet />,
        children: [
          {
            path: '/sanction/report',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SANCTION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ReportPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/sanction/restrict',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SANCTION_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <RestrictPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/database',
        element: <Outlet />,
        children: [
          {
            path: '/database/item',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ItemPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/university',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <UniversityPage />
                </Suspense>
              </LoginRequired>
            ),
            children: [
              {
                path: '/database/university/:universityId',
                element: (
                  <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                    <Suspense fallback={<>...loading</>}>
                      <UserDetailPage />
                    </Suspense>
                  </LoginRequired>
                ),
              },
            ],
          },
          {
            path: '/database/highschool',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <HighSchoolPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/country',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <CountryPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/badge',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <BadgePage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/admissionUserCount',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <AdmissionsPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/standardizedSubject',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <StandarizedSubjectPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/database/statisticsMajorCategory',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.DATABASE_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsMajorCategoryPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },

      {
        path: '/system',
        element: <Outlet />,
        children: [
          {
            path: '/system/setting',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SYSTEM_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <SettingPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/system/app-push',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.SYSTEM_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <AppPushAlarmPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/statistics',
        element: <Outlet />,
        children: [
          {
            path: '/statistics/user-admissions',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.STATISTICS]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsUserAdmissionPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/statistics/user-admissions/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.STATISTICS]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsUserAdmissionDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/statistics/user-admissions/upload-logs',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.STATISTICS_LOGS]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsExcelUploadLogsPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/statistics/user-admissions/upload-logs/:id',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.STATISTICS_LOGS]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsExcelUploadLogDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/statistics/user-admissions/download-logs',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.STATISTICS_LOGS]}>
                <Suspense fallback={<>...loading</>}>
                  <StatisticsExcelDownloadLogsPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/problem-solved',
        element: <Outlet />,
        children: [
          {
            path: '/problem-solved',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ProblemSolvedManagePage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/problem-solved/question/create',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <QuestionFormPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/problem-solved/question/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <QuestionFormPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/problem-solved/errors',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ProblemErrorPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/exam-round',
        element: <Outlet />,
        children: [
          {
            path: '/exam-round',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ExamRoundListPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/exam-round/create',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ExamRoundFormPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/exam-round/:id/edit',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ExamRoundFormPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
      {
        path: '/problem-solving-sessions',
        element: <Outlet />,
        children: [
          {
            path: '/problem-solving-sessions',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ProblemSolvingSessionListPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/problem-solving-sessions/dashboard',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ProblemSolvingSessionDashboardPage />
                </Suspense>
              </LoginRequired>
            ),
          },
          {
            path: '/problem-solving-sessions/:sessionId',
            element: (
              <LoginRequired requiredPermissions={[PermissionKey.PROBLEM_SOLVED_MANAGEMENT]}>
                <Suspense fallback={<>...loading</>}>
                  <ProblemSolvingSessionDetailPage />
                </Suspense>
              </LoginRequired>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<>...loading</>}>
        <LoginPage />
      </Suspense>
    ),
  },
];
